import { useState, type ReactNode } from 'react';
import { t } from '@hana/i18n';
import '../strings.i18n';
import { useToast } from '@hana/ui';
import { BKUPS, OWNERS, RULES, ROUTES, ROSTER, type Tab } from './data';
import s from './admin.module.css';

// Bảng của các tab task_rule · mail_label_route · duty_roster · customer_bkg_owner · staff_backup (Mint-Admin, view params).
// Công tắc chỉ đổi trạng thái trên máy + toast; không ghi DB.

type Cols = { cols: string; heads: string[]; rows: ReactNode[][]; foot: string };
const Cell = ({ k, sub }: { k: ReactNode; sub?: string }) => (
  <div>
    <div className={s.k} style={{ fontWeight: 'inherit' }}>{k}</div>
    {sub && <div className={s.sub}>{sub}</div>}
  </div>
);
const Chip = ({ children, tone }: { children: ReactNode; tone?: 'warn' | 'pri' }) => <span className={`${s.cipt} ${tone ? s[tone] : ''}`}>{children}</span>;

export function TabTable({ tab }: { tab: Exclude<Tab, 'param'> }) {
  const toast = useToast();
  const [on, setOn] = useState<Record<string, boolean>>({});
  const Sw = ({ id, d, label }: { id: string; d: boolean; label: string }) => {
    const v = on[id] ?? d;
    return (
      <button type="button" role="switch" aria-checked={v} aria-label={label} className={s.sw} onClick={() => { setOn((o) => ({ ...o, [id]: !v })); toast({ msg: `${label}: ${v ? 'tắt' : 'bật'} (bản mẫu, chưa ghi)` }); }}>
        <span />
      </button>
    );
  };
  const none = (v: string) => (v.startsWith('—') && v !== '—' ? <Chip tone="warn">{t('kit.x.chuaCo')}</Chip> : v);
  const T: Record<Exclude<Tab, 'param'>, Cols> = {
    rule: { cols: '1.3fr 1.3fr 1.4fr 1.1fr 1fr 60px', heads: ['Quy tắc', 'Khi', 'Điều kiện', 'Giao ghế', 'Hạn', 'Bật'], foot: '6 quy tắc · 2.140 việc sinh tháng này',
      rows: RULES.map(([id, k, when, cond, to, due, own, d]) => [<Cell key="a" k={k} sub={`${id} · chủ ${own}`} />, when, cond, to, due, <Sw key="s" id={`r${id}`} d={d} label={`Bật quy tắc ${k}`} />]) },
    route: { cols: '1.1fr 80px 1.6fr 1.2fr 1.2fr 60px', heads: ['Nhãn', 'Hộp', 'Điều kiện', 'Giao', 'Fallback', 'Bật'], foot: '6 nhãn · 312 mail định tuyến hôm qua · 4 rơi về fallback',
      rows: ROUTES.map(([k, box, cond, to, fb, d]) => [<Cell key="a" k={k} />, <Chip key="b">{box}</Chip>, cond, to, fb, <Sw key="s" id={`m${k}`} d={d} label={`Bật nhãn ${k}`} />]) },
    roster: { cols: '1.3fr 0.8fr 1fr 1fr 1.6fr', heads: ['Ngày', 'Ca', 'Trực chính', 'Dự phòng', 'Ghi chú'], foot: 'Tuần 43 · 3 người trực · đổi ca cần Trưởng TRF xác nhận',
      rows: ROSTER.map(([d, sh, a, b, n]) => [<Cell key="a" k={d} />, <Chip key="b" tone={sh === 'Trực nghỉ' ? 'warn' : 'pri'}>{sh}</Chip>, a, b, n || '—']) },
    owner: { cols: '1.6fr 0.8fr 1fr 1fr 1fr', heads: ['Khách', 'Sale', 'Chủ booking', 'Dự phòng', 'Hiệu lực'], foot: '124 khách có chủ booking · 9 chưa có dự phòng',
      rows: OWNERS.map(([c, sa, o, b, e]) => [<Cell key="a" k={c} />, sa, none(o), none(b), `từ ${e}`]) },
    backup: { cols: '0.8fr 1.3fr 1fr 1.4fr 0.9fr', heads: ['Người', 'Ghế', 'Backup', 'Uỷ quyền', 'Đến'], foot: '46 nhân sự · 2 uỷ quyền đang hiệu lực',
      rows: BKUPS.map(([p, se, b, d, e]) => [<Cell key="a" k={p} />, se, b, d === '—' ? '—' : <Chip key="b" tone="warn">{d}</Chip>, e || '—']) },
  };
  const { cols, heads, rows, foot } = T[tab];
  return (
    <>
      <div role="table" aria-label={tab} className={s.tbl}>
        <div role="row" className={`${s.tr} ${s.th}`} style={{ gridTemplateColumns: cols }}>
          {heads.map((h) => <span key={h} role="columnheader">{h}</span>)}
        </div>
        {rows.map((r, i) => (
          <div key={i} role="row" className={s.tr} style={{ gridTemplateColumns: cols }}>
            {r.map((c, j) => <div key={j} role="cell">{c}</div>)}
          </div>
        ))}
      </div>
      <div className={s.foot}>{foot}</div>
    </>
  );
}
